import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { useQuery } from '@tanstack/react-query';
import { TextField } from '@warp-ds/react';
import debounce from 'debounce';
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { type UseControllerProps, useController, useFormContext, useWatch } from 'react-hook-form';
import type { PeliasResponse } from '../../../types/pelias.js';
import type { StreetWidgetModel } from '../../../types/widget.js';
import { fetcher } from '../../utils/fetcher.js';
import { getHuckConfig } from '../../utils/getHuckConfig.js';
import { Item } from '../combobox.js';

type StreetWidgetProps = Omit<StreetWidgetModel, 'type'>;

const useDebouncedOptions = (name: string, enabled: boolean, delay = 500) => {
  const config = getHuckConfig();
  const value = useWatch({ name });
  const [debouncedValue, setDebouncedValue] = useState('');

  const debouncedUpdate = useCallback(
    debounce((value) => {
      setDebouncedValue(value);
    }, delay),
    [],
  );

  useEffect(() => {
    debouncedUpdate(value);
  }, [value, debouncedUpdate]);

  const { data, isError } = useQuery<PeliasResponse>({
    queryKey: ['pelias-search', debouncedValue],
    queryFn: () => fetcher(`${config.root}/api/street?text=${encodeURIComponent(debouncedValue)}`),
    enabled: !!debouncedValue && debouncedValue.length >= 3 && enabled,
    retry: 0,
  });

  return debouncedValue === value && !isError ? data : undefined;
};

export const StreetWidget = ({ name, helpText, rules, label, mandatory }: StreetWidgetProps & UseControllerProps) => {
  const enableStreetFetch = useRef(false);
  const { control, setValue } = useFormContext();
  const { field, fieldState } = useController({ control, name, rules });
  const data = useDebouncedOptions(name, enableStreetFetch.current);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const virtualItemRef = useRef<HTMLElement | null>(null);

  const { refs, x, y, strategy, context } = useFloating<HTMLInputElement>({
    placement: 'bottom-start',
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: false }),
    open,
    onOpenChange: setOpen,
    middleware: [
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.min(availableHeight, 400)}px`,
          });
        },
        padding: 16,
      }),
    ],
  });

  const role = useRole(context, { role: 'listbox' });
  const dismiss = useDismiss(context);
  const listNav = useListNavigation(context, {
    listRef,
    virtualItemRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([role, dismiss, listNav]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    field.onChange(value);
    enableStreetFetch.current = true;

    // Clear lat, lon and accuracy when the user edits the street field
    setValue('address.latitude', '');
    setValue('address.longitude', '');
    setValue('address.accuracy', 5);

    if (value) {
      setOpen(true);
      setActiveIndex(0);
    } else {
      setOpen(false);
    }
  };

  const features = data?.features ?? [];

  const options = features.map((feature) => ({
    label: `${feature.properties.name}, ${feature.properties.postalcode.padStart(4, '0')}, ${feature.properties.locality ?? feature.properties.localadmin ?? feature.properties.region}`,
    value: feature.properties.name,
  }));

  const handleSelectSuggestion = (index: number) => {
    const feature = features[index];
    enableStreetFetch.current = false; // Disable fetching when a suggestion is selected

    // No need to invoke if we cannot find the result or if the value is what we already have stored
    if (!feature) return;

    const { postalcode } = feature.properties;
    setValue('address.postalcode', postalcode.padStart(4, '0'));

    const [lon, lat] = feature.geometry.coordinates;
    if (lat && lon) {
      setValue('address.latitude', lat.toString());
      setValue('address.longitude', lon.toString());
      setValue('address.accuracy', 9);
    }

    setValue(name, feature.properties.name); // This will not invoke onChange, ensuring that we do not fetch suggestions for the value in the selected suggestion
  };

  if (fieldState.error) {
    helpText = (fieldState.error.message as string) ?? 'Validation error';
  }

  return (
    <>
      <TextField
        {...getReferenceProps({
          label,
          onChange,
          value: field.value,
          'aria-autocomplete': 'list',
          onKeyDown(event) {
            if (event.key === 'Enter' && activeIndex != null && options[activeIndex]) {
              event.preventDefault();
              event.stopPropagation();
              handleSelectSuggestion(activeIndex);
              setActiveIndex(null);
              setOpen(false);
            }
          },
        })}
        ref={refs.setReference}
        helpText={helpText}
        optional={!mandatory}
        invalid={!!fieldState.error}
        data-testid={name}
      />
      {open ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} initialFocus={-1} visuallyHiddenDismiss>
            <div
              {...getFloatingProps({
                ref: refs.setFloating,
                style: {
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
                  overflowY: 'auto',
                  border: '1px solid lightgrey',
                  borderRadius: '5px',
                  background: 'white',
                  zIndex: 30,
                  display: options.length > 0 ? 'flex' : 'none',
                  flexDirection: 'column',
                  boxShadow: '0px 2px 8px 0px rgba(36, 50, 64, 0.08)',
                },
              })}
            >
              {options.map((item, index) => (
                <Item
                  key={item.label}
                  {...getItemProps({
                    ref(node) {
                      listRef.current[index] = node;
                    },
                    onClick() {
                      handleSelectSuggestion(index);
                      setOpen(false);
                      refs.domReference.current?.focus();
                    },
                    role: 'option',
                    tabIndex: activeIndex === index ? 0 : -1,
                  })}
                  active={activeIndex === index}
                >
                  {item.label}
                </Item>
              ))}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </>
  );
};
